import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FunctionComponent, ReactNode } from "react";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { Tooltip } from "@/components/Tooltip/Tooltip";

export const InfoTag: FunctionComponent<{
	label: string;
	icon: IconDefinition;
	noIcon?: boolean;
	iconSpin?: boolean;
	style: "success" | "error" | "warning" | "info" | "action";
	tooltip?: string | ReactNode;
	hoverLabel?: string;
	hoverIcon?: IconDefinition;
}> = ({ label, icon, style, tooltip, noIcon = false, iconSpin = false, hoverIcon, hoverLabel }) => {
	const hasHover = !!(hoverIcon && hoverLabel);

	const containerStyles = {
		success:
			"border-status-success/50 bg-status-success-light/50 hover:bg-status-success hover:text-white",
		error: "border-status-error/50 bg-status-error-light/50 hover:bg-status-error hover:text-white",
		warning:
			"border-status-warning/50 bg-status-warning-light hover:bg-status-warning hover:text-white",
		info: "border-status-info/50 bg-status-info-light/50 hover:bg-status-info hover:text-white",
		action: "border-blue-action/50 bg-blue-action/10 hover:bg-blue-action hover:text-white",
	};

	const iconStyles = {
		success:
			"text-status-success bg-status-success/20 group-hover:text-status-success group-hover:bg-status-success-light",
		error:
			"text-status-error bg-status-error/20 group-hover:text-status-error-light group-hover:bg-status-error-light/30",
		warning:
			"text-status-warning bg-status-warning/20 group-hover:text-status-warning-light group-hover:bg-status-warning-light/30",
		info: "text-status-info bg-status-info/20 group-hover:text-status-info-light group-hover:bg-status-info-light/30",
		action:
			"text-blue-action bg-blue-action/20 group-hover:text-blue-action-200 group-hover:bg-blue-action-200/30",
	};

	const textStyles = {
		success: "text-status-success group-hover:text-status-success-light",
		error: "text-status-error group-hover:text-status-error-light",
		warning: "text-status-warning group-hover:text-status-warning-light",
		info: "text-status-info group-hover:text-status-info-light",
		action: "text-blue-action group-hover:text-blue-action-200",
	};

	return (
		<Tooltip content={tooltip || ""} disabled={!tooltip}>
			<span
				className={classNames(
					"mr-1 mb-1 px-2 overflow-hidden group whitespace-nowrap py-1 border rounded-lg text-sm text-left inline-flex flex-row gap-1 items-center transition-colors min-w-0",
					hasHover ? "cursor-pointer" : "cursor-default!",
					containerStyles[style]
				)}
			>
				{hasHover ? (
					<div className="relative inline-flex items-center min-w-0">
						<span className="invisible inline-flex items-center gap-1 min-w-0">
							{!noIcon && (
								<Icon className="rounded-full p-[3px] w-4 h-4 mr-1 shrink-0" icon={icon} />
							)}
							<span className="truncate max-w-[300px]">{label}</span>
						</span>

						<span className="absolute inset-0 inline-flex items-center gap-1 opacity-100 group-hover:opacity-0 transition-opacity min-w-0">
							{!noIcon && (
								<Icon
									className={classNames(
										"rounded-full p-[3px] w-4 h-4 mr-1 shrink-0",
										iconStyles[style]
									)}
									icon={icon}
								/>
							)}
							<span className={classNames("truncate max-w-[300px]", textStyles[style])}>
								{label}
							</span>
						</span>

						<span className="absolute inset-0 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity min-w-0">
							<Icon
								className={classNames(
									"rounded-full p-[3px] w-4 h-4 mr-1 shrink-0",
									iconStyles[style]
								)}
								icon={hoverIcon}
							/>
							<span className="truncate max-w-[300px]">{hoverLabel}</span>
						</span>
					</div>
				) : (
					<>
						{!noIcon && (
							<Icon
								className={classNames(
									"rounded-full p-[3px] w-4 h-4 mr-1 shrink-0",
									iconStyles[style]
								)}
								icon={icon}
								spin={iconSpin}
							/>
						)}
						<span className={classNames("truncate max-w-[300px]", textStyles[style])}>{label}</span>
					</>
				)}
			</span>
		</Tooltip>
	);
};
